import type { ICommand } from '@uiw/react-md-editor/nohighlight';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
  type ReactNode
} from 'react';
import '@uiw/react-md-editor/markdown-editor.css';

import { Tabs } from '@domcrm/ui';
import { useStaffColorMode } from '../hooks/useStaffColorMode';
import { markdownToSafeHtml } from '../lib/markdown-agreement-html';
import {
  MARKDOWN_EDITOR_AUTO_HEIGHT,
  measureMarkdownEditorTotalHeightPx,
  markdownEditorTextMaxPx
} from '../lib/markdown-editor-height';
import { cn } from '../lib/cn';
import {
  MarkdownEditorTextareaContext,
  type MarkdownEditorTextareaContextValue
} from './markdown-editor-textarea-context';
import { StaffMarkdownDiffView } from './StaffMarkdownDiffView';
import type { VersionEntry } from '../lib/markdown-line-diff';

const MDEditor = lazy(async () => {
  const mod = await import('@uiw/react-md-editor/nohighlight');
  return { default: mod.default };
});

const MD_TOOLBAR_OMIT = new Set(['code', 'codeBlock', 'comment', 'image', 'help']);

export type StaffTrainerMarkdownFieldProps = {
  label?: ReactNode;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  editorHeight?: number | 'auto';
  /** Минимальная высота зоны текста (без тулбара), px; по умолчанию из `MARKDOWN_EDITOR_AUTO_HEIGHT`. */
  textMinPx?: number;
  transformPreviewMarkdown?: (markdown: string) => string;
  endLabelSlot?: ReactNode;
  editorToolbarEndSlot?: ReactNode;
  editorTextAreaRef?: MutableRefObject<HTMLTextAreaElement | null>;
  /** Классы панели предпросмотра (Markdown → HTML). */
  previewPanelClassName?: string;
  /** Серверная версия текста для показа diff (вкладка «Изменения»). */
  diffBaseline?: string;
  /** История версий для выбора при сравнении. */
  diffVersions?: VersionEntry[];
  dataTestId?: string;
};

export const StaffTrainerMarkdownField: FC<StaffTrainerMarkdownFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  className,
  editorHeight = 'auto',
  textMinPx: textMinPxProp,
  transformPreviewMarkdown,
  endLabelSlot,
  editorToolbarEndSlot,
  editorTextAreaRef,
  previewPanelClassName,
  diffBaseline,
  diffVersions,
  dataTestId
}) => {
  const baseId = useId();
  const colorMode = useStaffColorMode();
  const diffEnabled = diffBaseline !== undefined;
  const [tab, setTab] = useState<'edit' | 'preview' | 'diff'>('edit');
  const previewMd = transformPreviewMarkdown ? transformPreviewMarkdown(value) : value;
  const previewHtml = useMemo(() => markdownToSafeHtml(previewMd), [previewMd]);
  const fixedHeight = typeof editorHeight === 'number' ? editorHeight : null;
  const toolbarApproxPx = 52;
  const textMinPx = textMinPxProp ?? MARKDOWN_EDITOR_AUTO_HEIGHT.textMinPx;
  const editorRootRef = useRef<HTMLDivElement>(null);
  const [viewportH, setViewportH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  const [contentTotalPx, setContentTotalPx] = useState(() => toolbarApproxPx + textMinPx);

  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const textMaxPx = markdownEditorTextMaxPx(viewportH);

  useLayoutEffect(() => {
    if (fixedHeight !== null) return;
    if (tab !== 'edit') return;
    const root = editorRootRef.current;
    if (!root) return;

    const sync = (): void => {
      const measured = measureMarkdownEditorTotalHeightPx(root, toolbarApproxPx, textMinPx, textMaxPx);
      if (measured != null) setContentTotalPx(measured);
    };

    sync();
    const id0 = requestAnimationFrame(() => {
      sync();
      requestAnimationFrame(sync);
    });
    const t = window.setTimeout(sync, 0);
    return () => {
      cancelAnimationFrame(id0);
      window.clearTimeout(t);
    };
  }, [value, tab, fixedHeight, textMaxPx, textMinPx]);

  const resolvedEditorHeight = fixedHeight ?? contentTotalPx;
  const textAreaMinHeight = Math.max(textMinPx, resolvedEditorHeight - toolbarApproxPx);

  const getTextArea = useCallback((): HTMLTextAreaElement | null => {
    if (tab !== 'edit') return null;
    const root = editorRootRef.current;
    if (!root) return null;
    const el = root.querySelector('textarea');
    return el instanceof HTMLTextAreaElement ? el : null;
  }, [tab]);

  useLayoutEffect(() => {
    if (!editorTextAreaRef) return;
    editorTextAreaRef.current = getTextArea();
  }, [editorTextAreaRef, getTextArea, value, resolvedEditorHeight, contentTotalPx]);

  const markdownEditorTextareaCtx = useMemo(
    (): MarkdownEditorTextareaContextValue => ({ getTextArea }),
    [getTextArea]
  );

  const toolbarExtraCommands = useMemo((): ICommand[] => {
    if (!editorToolbarEndSlot) return [];
    return [
      {
        keyCommand: 'domcrm-toolbar-end',
        name: 'domcrmToolbarEnd',
        liProps: { 'aria-label': 'Дополнительные действия редактора' },
        render: () => (
          <span className="flex items-center gap-1 py-0.5" onMouseDown={(e) => e.preventDefault()}>
            {editorToolbarEndSlot}
          </span>
        )
      }
    ];
  }, [editorToolbarEndSlot]);

  const editBlock = (
    <div
      ref={editorRootRef}
      className="domcrm-md-editor w-full max-w-full overflow-hidden rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))]"
      data-color-mode={colorMode}
    >
      <Suspense
        fallback={
          <div
            className="flex min-h-[140px] items-center justify-center rounded-[var(--ui-radius)] bg-[hsl(var(--ui-muted))] px-3 text-sm text-[hsl(var(--ui-muted-foreground))]"
            aria-busy="true"
          >
            Загрузка редактора…
          </div>
        }
      >
        <MDEditor
          value={value}
          onChange={(v) => {
            onChange(v ?? '');
          }}
          overflow={false}
          preview="edit"
          visibleDragbar={false}
          height={resolvedEditorHeight}
          minHeight={textAreaMinHeight}
          hideToolbar={disabled}
          extraCommands={toolbarExtraCommands}
          highlightEnable={false}
          commandsFilter={(command) => {
            const n = command.name;
            if (n && MD_TOOLBAR_OMIT.has(n)) return false;
            return command;
          }}
          textareaProps={{
            id: `${baseId}-md`,
            readOnly: disabled,
            placeholder: placeholder ?? 'Заголовок\n\n- пункт списка\n- **жирный** текст',
            spellCheck: true
          }}
        />
      </Suspense>
    </div>
  );

  const previewBlock = (
    <div
      className={cn(
        'domcrm-md-preview-panel box-border overflow-auto overscroll-y-auto rounded-[var(--ui-radius)] border border-[hsl(var(--ui-border))] px-3 py-3 text-sm leading-relaxed text-[hsl(var(--ui-foreground))]',
        '[&_a]:text-[hsl(var(--ui-primary))] [&_a]:underline [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-[hsl(var(--ui-border))] [&_blockquote]:pl-3 [&_blockquote]:text-[hsl(var(--ui-muted-foreground))]',
        '[&_code]:rounded [&_code]:bg-[hsl(var(--ui-muted))] [&_code]:px-1 [&_code]:text-sm',
        '[&_h1]:mb-3 [&_h1]:scroll-mt-2 [&_h1]:border-b [&_h1]:border-[hsl(var(--ui-border))] [&_h1]:pb-2 [&_h1]:text-xl [&_h1]:font-semibold',
        '[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:scroll-mt-2 [&_h2]:text-lg [&_h2]:font-semibold',
        '[&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:text-base [&_h3]:font-semibold',
        '[&_li]:my-0.5 [&_ol]:my-2 [&_ol]:ml-5 [&_ol]:list-decimal [&_ol]:space-y-0.5 [&_p]:mb-2 [&_p]:max-w-[85ch]',
        '[&_pre]:my-2 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-[hsl(var(--ui-muted))] [&_pre]:p-2 [&_pre]:text-sm',
        '[&_ul]:my-2 [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-0.5',
        'staff-agreement-md-preview',
        previewPanelClassName
      )}
      style={{ height: resolvedEditorHeight }}
      dangerouslySetInnerHTML={{ __html: previewHtml }}
    />
  );

  return (
    <MarkdownEditorTextareaContext.Provider value={markdownEditorTextareaCtx}>
      <div className={cn('flex min-h-0 flex-col gap-2', className)} data-testid={dataTestId}>
        {label || endLabelSlot ? (
          <div className="flex flex-wrap items-start justify-between gap-2">
            {label ? (
              <label className="block min-w-0 flex-1 text-sm text-[hsl(var(--ui-muted-foreground))]" htmlFor={`${baseId}-md`}>
                {label}
              </label>
            ) : null}
            {endLabelSlot ? <div className="shrink-0">{endLabelSlot}</div> : null}
          </div>
        ) : null}
        <Tabs
          items={[
            { value: 'edit', label: 'Редактор', content: <div className="pt-1">{editBlock}</div> },
            { value: 'preview', label: 'Предпросмотр', content: <div className="pt-1">{previewBlock}</div> },
            ...(diffEnabled
              ? [
                  {
                    value: 'diff' as const,
                    label: 'Изменения',
                    content: (
                      <div className="pt-1">
                        <StaffMarkdownDiffView
                          oldText={diffBaseline ?? ''}
                          newText={value}
                          height={typeof editorHeight === 'number' ? editorHeight : undefined}
                          versions={diffVersions}
                        />
                      </div>
                    )
                  }
                ]
              : [])
          ]}
          value={tab}
          onValueChange={(v) => setTab(v as 'edit' | 'preview' | 'diff')}
          panelClassName="min-h-0 pt-0"
          listClassName="w-full max-w-none"
        />
      </div>
    </MarkdownEditorTextareaContext.Provider>
  );
};
